import { useState, useCallback } from 'react';
import {
  type Metadata,
  type StageState,
  initStageState,
  isItemApplicable,
  INSPECTION_ITEMS,
  INSPECTION_STAGES,
} from '@skd/shared';

const defaultMetadata: Metadata = {
  container_origins: 'PORT',
  container_origins_other: '',
  inside_container: 'OK',
  inside_container_other: '',
  model: 'e245',
  model_other: '',
  invoice_no: '408',
  case_no: '10',
  box_no: '408',
  body_number: '',
};

export function useInspection() {
  const [metadata, setMetadata] = useState<Metadata>({ ...defaultMetadata });
  const [stages, setStages] = useState<Record<number, StageState>>(initStageState());
  const [currentStep, setCurrentStep] = useState(0);

  const resetForm = useCallback(() => {
    setMetadata({ ...defaultMetadata });
    setStages(initStageState());
    setCurrentStep(0);
  }, []);

  const updateMetadata = useCallback((field: keyof Metadata, value: string) => {
    setMetadata((prev) => ({ ...prev, [field]: value }));
  }, []);

  const loadRecord = useCallback((record: any) => {
    setMetadata({ ...defaultMetadata, ...record.metadata });
    setStages(record.stages);
    setCurrentStep(0);
  }, []);

  const toggleItemStatus = useCallback((stageId: number, itemId: string) => {
    setStages((prev) => {
      if (!isItemApplicable(itemId, stageId)) return prev;
      const stage = { ...prev[stageId] };
      const items = { ...stage.items };
      const item = { ...items[itemId] };

      if (item.status === 'OK') {
        item.status = 'NG';
        if (!item.defectType) item.defectType = 'Scratch';
      } else if (item.status === 'NG') {
        item.status = 'OK';
        item.defectType = '';
        item.remark = '';
      }

      items[itemId] = item;
      stage.items = items;
      // Recompute from the updated items every time, rather than patching the
      // old flag — patching missed the case where this toggle clears the last
      // remaining NG (it checked the stale pre-toggle status of this same item).
      stage.actionStatus = Object.values(items).some((i: any) => i.status === 'NG')
        ? 'ACTION REQ'
        : 'SKD Body OK';
      return { ...prev, [stageId]: stage };
    });
  }, []);

  const setDefectType = useCallback((stageId: number, itemId: string, defectType: string) => {
    setStages((prev) => {
      const stage = { ...prev[stageId] };
      const items = { ...stage.items };
      items[itemId] = { ...items[itemId], defectType };
      stage.items = items;
      return { ...prev, [stageId]: stage };
    });
  }, []);

  const updateItemRemark = useCallback((stageId: number, itemId: string, remark: string) => {
    setStages((prev) => {
      const stage = { ...prev[stageId] };
      const items = { ...stage.items };
      items[itemId] = { ...items[itemId], remark };
      stage.items = items;
      return { ...prev, [stageId]: stage };
    });
  }, []);

  const updateStageField = useCallback((stageId: number, field: keyof StageState, value: any) => {
    setStages((prev) => {
      const stage = { ...prev[stageId], [field]: value };
      return { ...prev, [stageId]: stage };
    });
  }, []);

  const markStageSaved = useCallback((stageId: number, savedBy: string) => {
    const now = new Date();
    const dateStr = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const stage = {
      ...stages[stageId],
      inspectorFure: savedBy,
      saved: true,
      savedAt: dateStr,
      savedBy,
    };
    const nextStages = { ...stages, [stageId]: stage };
    setStages(nextStages);
    return nextStages;
  }, [stages]);

  const getStageItems = useCallback((stageId: number) => {
    return INSPECTION_ITEMS.filter((item) => isItemApplicable(item.id, stageId));
  }, []);

  const getItemState = useCallback((stageId: number, itemId: string) => {
    return stages[stageId]?.items[itemId] || null;
  }, [stages]);

  return {
    metadata,
    stages,
    currentStep,
    updateMetadata,
    setCurrentStep,
    resetForm,
    loadRecord,
    toggleItemStatus,
    setDefectType,
    updateItemRemark,
    updateStageField,
    markStageSaved,
    getStageItems,
    getItemState,
    setMetadata,
  };
}
